import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Calendar,
  Check,
  ChevronRight,
  Clock3,
  Copy,
  FileText,
  Gavel,
  Mail,
  MessageCircle,
  MessageSquare,
  Monitor,
  MoreHorizontal,
  Package,
  Plus,
  ReceiptText,
  Settings,
  ShieldCheck,
  Smartphone,
  Trash2,
  TrendingUp,
  Users,
  Tag,
  Building2,
  HardHat,
  X,
} from 'lucide-react';
import { FeatureLimitHint } from '../../../components/FeatureLimitHint';
import { useFeatureLimits } from '../../../hooks/useFeatureLimits';
import {
  SETTINGS_PATH_ROLES,
  SETTINGS_ROLE_NEW_ID,
  roleIdFromSettingsPath,
  roleSettingsPath,
} from '../../../utils/settingsRoutes';
import { applyCapabilityImplications } from '../../../lib/capabilities/implications';
import {
  DEFAULT_GEOFENCE_RADIUS_M,
  GEOFENCE_CAPABILITY_KEYS,
  MAX_GEOFENCE_RADIUS_M,
  MIN_GEOFENCE_RADIUS_M,
  clampGeofenceRadiusM,
  geofenceRadiusMValidationError,
  parseGeofenceRadiusM,
} from '../../../lib/capabilities/geofence';
import {
  DEFAULT_EARLY_CLOCK_IN_MINUTES,
  MAX_EARLY_CLOCK_IN_MINUTES,
  MIN_EARLY_CLOCK_IN_MINUTES,
  clampEarlyClockInMinutes,
  earlyClockInMinutesValidationError,
  parseEarlyClockInMinutes,
} from '../../../lib/capabilities/earlyClockIn';
import {
  CLOCK_ACTIVITY_MODE_AVAILABLE,
  DEFAULT_CLOCK_MODE,
  parseClockMode,
  type ClockMode,
} from '../../../lib/capabilities/clockMode';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { appGroupsFor, webGroupsFor, type RoleEditorCapabilityGroup } from '../roleEditorGroups';
import { useDatevStatus } from '../../datev/useDatevStatus';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { DeleteRolePresetDialog } from '../components/DeleteRolePresetDialog';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
  SETTINGS_TEXTAREA_CLASS,
} from '../utils/settingsSectionStyles';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { listCapabilityDeviations } from '../capabilitiesUi';

type PlatformAccess = 'office_and_field' | 'field_only';
type CapabilityMap = Record<string, boolean>;
type SurfaceTab = 'web' | 'app';

type RolePreset = {
  id: string;
  name: string;
  description: string;
  default_platform_access: PlatformAccess;
  capabilities: CapabilityMap;
  /** Radius for the „nur vor Ort“ Zeiterfassung capabilities. */
  geofence_radius_m: number;
  /** Minutes before first appointment when clock-in is allowed. */
  early_clock_in_minutes: number;
  clock_mode: ClockMode;
  forked_from_key: string | null;
  is_system_fork: boolean;
  assigned_user_count?: number;
};

/** Draft while typing so number fields can be cleared (empty string) before blur-clamp. */
type EditingRolePreset = Omit<RolePreset, 'geofence_radius_m' | 'early_clock_in_minutes'> & {
  geofence_radius_m: number | '';
  early_clock_in_minutes: number | '';
};

function initialSurfaceForAccess(access: PlatformAccess): { tab: SurfaceTab; groupKey: string } {
  if (access === 'field_only') return { tab: 'app', groupKey: 'zeit' };
  return { tab: 'web', groupKey: 'projekte' };
}

type CapabilityGroup = RoleEditorCapabilityGroup;

const EMPTY_PRESET: RolePreset = {
  id: '',
  name: '',
  description: '',
  default_platform_access: 'office_and_field',
  capabilities: {},
  geofence_radius_m: DEFAULT_GEOFENCE_RADIUS_M,
  early_clock_in_minutes: DEFAULT_EARLY_CLOCK_IN_MINUTES,
  clock_mode: DEFAULT_CLOCK_MODE,
  forked_from_key: null,
  is_system_fork: false,
  assigned_user_count: 0,
};

type RoleMember = {
  user_id: string;
  display_name: string | null;
  email: string;
  role_preset_id?: string | null;
  capability_overrides?: CapabilityMap;
  invited?: boolean;
};

function roleMemberName(member: RoleMember): string {
  return member.display_name?.trim() || member.email?.trim() || 'Unbenannt';
}

const hasGeofenceCapability = (caps: CapabilityMap): boolean =>
  GEOFENCE_CAPABILITY_KEYS.some((key) => Boolean(caps[key]));

function normalizeCaps(input: CapabilityMap): CapabilityMap {
  return applyCapabilityImplications({ ...input });
}

function Toggle({
  active,
  disabled,
  onClick,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative h-6 w-10 shrink-0 rounded-full transition-colors ${active ? 'bg-pipedrive-blue' : 'bg-slate-200'} disabled:cursor-not-allowed disabled:opacity-45`}
    >
      <span
        className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-app-surface shadow-sm transition-transform ${
          active ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

/** Counts groups with at least one enabled capability. When `inactive`, returns 0 (e.g. Web under Nur App). */
function countEnabledGroups(groups: CapabilityGroup[], caps: CapabilityMap, inactive = false): number {
  if (inactive) return 0;
  return groups.filter((group) => group.perms.some((perm) => Boolean(caps[perm.key]))).length;
}

function countTotalGroups(groups: CapabilityGroup[]): number {
  return groups.length;
}

function statusLabelForGroup(group: CapabilityGroup, caps: CapabilityMap): string {
  const enabled = group.perms.filter((perm) => Boolean(caps[perm.key]));
  if (!enabled.length) return 'Kein Zugriff';
  if (enabled.length === 1) return enabled[0]?.label ?? 'Zugriff';
  return enabled.map((perm) => perm.label).join(' • ');
}

type FieldErrors = {
  geofence_radius_m?: string;
  early_clock_in_minutes?: string;
};

function SettingsErrorBanner({
  message,
  onDismiss,
  className = '',
}: {
  message: string;
  onDismiss: () => void;
  className?: string;
}) {
  return (
    <div
      className={`flex items-start justify-between gap-3 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 ${className}`}
    >
      <span className="min-w-0">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 rounded-md p-1 text-red-500 hover:bg-red-100 hover:text-red-700"
        aria-label="Fehlermeldung schließen"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function invalidNumberInputClass(hasError: boolean): string {
  return hasError
    ? 'border-red-500 bg-red-50 text-red-900 focus:border-red-500 focus:ring-2 focus:ring-red-200'
    : 'border-app-border text-app-text focus:border-pipedrive-blue';
}

const GROUP_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  projekte: TrendingUp,
  rechnungen: ReceiptText,
  kommentare: MessageCircle,
  kontakte: Users,
  plantafel: Calendar,
  posteingang: Mail,
  kichat: MessageSquare,
  stammdaten: Package,
  ausschreibungen: Gavel,
  vorlagen: FileText,
  hr: Users,
  einstellungen: Settings,
  zeit: Clock3,
  preise: Tag,
};

function Chip({
  label,
  tone,
  icon: Icon,
}: {
  label: string;
  tone: 'green' | 'amber' | 'blue' | 'violet' | 'slate';
  icon?: React.ComponentType<{ className?: string }>;
}) {
  const toneClass = {
    green: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
    violet: 'bg-violet-100 text-violet-700',
    slate: 'bg-app-surface-muted text-app-text-muted',
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium ${toneClass}`}>
      {Icon ? <Icon className="h-3 w-3" /> : null}
      <span>{label}</span>
    </span>
  );
}

export function RolesRightsSettingsSection() {
  const navigate = useNavigate();
  const datev = useDatevStatus();
  const { modules, isEnabled: isModuleOn } = useModuleAccess();
  // DATEV-Recht nur bei aktiver Integration; Gruppen und Rechte abgewählter Module fehlen ganz.
  const WEB_GROUPS = React.useMemo(() => webGroupsFor(modules, { datevEnabled: datev.enabled }), [datev.enabled, modules]);
  const APP_GROUPS = React.useMemo(() => appGroupsFor(modules), [modules]);
  // Ohne Mobile-App-Modul gibt es keine App-Rechte zu vergeben.
  const fieldAppEnabled = isModuleOn('field_app');
  const location = useLocation();
  const instancePrefix = React.useMemo(() => {
    const match = location.pathname.match(/^\/i\/[^/]+/);
    return match ? match[0] : '/i/default';
  }, [location.pathname]);
  const plainPath = React.useMemo(() => location.pathname.replace(/^\/i\/[^/]+/, ''), [location.pathname]);
  const withInstancePath = React.useCallback(
    (path: string) => `${instancePrefix}${path.startsWith('/') ? path : `/${path}`}`,
    [instancePrefix],
  );
  const roleIdFromUrl = React.useMemo(() => roleIdFromSettingsPath(plainPath), [plainPath]);

  const [presets, setPresets] = React.useState<RolePreset[]>([]);
  const [members, setMembers] = React.useState<RoleMember[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({});
  const [saving, setSaving] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<SurfaceTab>('web');
  const [openGroupKey, setOpenGroupKey] = React.useState<string>('projekte');
  const [editing, setEditing] = React.useState<EditingRolePreset | null>(null);
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<RolePreset | null>(null);
  const openMenuRef = React.useRef<HTMLDivElement | null>(null);
  const { usage, loaded: limitsReady } = useFeatureLimits();

  const closeEditor = React.useCallback(() => {
    navigate(withInstancePath(SETTINGS_PATH_ROLES));
  }, [navigate, withInstancePath]);

  const dismissError = React.useCallback(() => {
    setError(null);
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setFieldErrors({});
    try {
      const [res, membersRes] = await Promise.all([
        fetch('/api/me/role-presets', { credentials: 'include' }),
        fetch('/api/me/members', { credentials: 'include' }),
      ]);
      const data = (await res.json().catch(() => null)) as { presets?: RolePreset[]; error?: string } | null;
      if (!res.ok) throw new Error(data?.error ?? 'Rollen konnten nicht geladen werden.');
      setPresets(data?.presets ?? []);
      // Only for the "who uses this role" list — the editor works without it.
      const membersData = membersRes.ok
        ? ((await membersRes.json().catch(() => null)) as { members?: RoleMember[] } | null)
        : null;
      setMembers(membersData?.members ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (!roleIdFromUrl) {
      setEditing(null);
      setFieldErrors({});
      return;
    }
    if (loading) return;

    if (roleIdFromUrl === SETTINGS_ROLE_NEW_ID) {
      const clonePreset = (location.state as { clonePreset?: RolePreset } | null)?.clonePreset;
      if (clonePreset) {
        const surface = initialSurfaceForAccess(clonePreset.default_platform_access);
        setActiveTab(surface.tab);
        setOpenGroupKey(surface.groupKey);
        setEditing({
          ...clonePreset,
          id: '',
          name: `${clonePreset.name} Kopie`,
          is_system_fork: false,
          forked_from_key: clonePreset.forked_from_key ?? clonePreset.id,
          early_clock_in_minutes:
            clampEarlyClockInMinutes(clonePreset.early_clock_in_minutes) ?? DEFAULT_EARLY_CLOCK_IN_MINUTES,
          geofence_radius_m:
            clampGeofenceRadiusM(clonePreset.geofence_radius_m) ?? DEFAULT_GEOFENCE_RADIUS_M,
          capabilities: normalizeCaps({ ...clonePreset.capabilities }),
          assigned_user_count: 0,
        });
      } else {
        const surface = initialSurfaceForAccess(EMPTY_PRESET.default_platform_access);
        setActiveTab(surface.tab);
        setOpenGroupKey(surface.groupKey);
        setEditing({ ...EMPTY_PRESET, capabilities: {} });
      }
      return;
    }

    const preset = presets.find((entry) => entry.id === roleIdFromUrl);
    if (!preset) {
      setError('Rolle nicht gefunden.');
      navigate(withInstancePath(SETTINGS_PATH_ROLES), { replace: true });
      return;
    }
    const surface = initialSurfaceForAccess(preset.default_platform_access);
    setActiveTab(surface.tab);
    setOpenGroupKey(surface.groupKey);
    setEditing({
      ...preset,
      early_clock_in_minutes:
        clampEarlyClockInMinutes(preset.early_clock_in_minutes) ?? DEFAULT_EARLY_CLOCK_IN_MINUTES,
      geofence_radius_m: clampGeofenceRadiusM(preset.geofence_radius_m) ?? DEFAULT_GEOFENCE_RADIUS_M,
      capabilities: normalizeCaps({ ...preset.capabilities }),
    });
  }, [roleIdFromUrl, presets, loading, location.state, navigate, withInstancePath]);

  React.useEffect(() => {
    if (!openMenuId) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (openMenuRef.current?.contains(event.target as Node)) return;
      setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [openMenuId]);

  const openNew = () => {
    navigate(withInstancePath(roleSettingsPath(SETTINGS_ROLE_NEW_ID)));
  };

  const openClone = (preset: RolePreset) => {
    navigate(withInstancePath(roleSettingsPath(SETTINGS_ROLE_NEW_ID)), { state: { clonePreset: preset } });
  };

  const openEdit = (preset: RolePreset) => {
    navigate(withInstancePath(roleSettingsPath(preset.id)));
  };

  const persist = async () => {
    if (!editing) return;
    setError(null);
    setFieldErrors({});

    const geofenceActive = hasGeofenceCapability(editing.capabilities);
    const clockEditActive = Boolean(editing.capabilities['app.zeit.edit']);
    const nextFieldErrors: FieldErrors = {};

    let geofenceRadiusM = clampGeofenceRadiusM(editing.geofence_radius_m) ?? DEFAULT_GEOFENCE_RADIUS_M;
    if (geofenceActive) {
      const radiusError = geofenceRadiusMValidationError(editing.geofence_radius_m);
      if (radiusError) {
        nextFieldErrors.geofence_radius_m = radiusError;
      } else {
        geofenceRadiusM = parseGeofenceRadiusM(editing.geofence_radius_m)!;
      }
    }

    let earlyClockInMinutes =
      clampEarlyClockInMinutes(editing.early_clock_in_minutes) ?? DEFAULT_EARLY_CLOCK_IN_MINUTES;
    if (clockEditActive) {
      const earlyError = earlyClockInMinutesValidationError(editing.early_clock_in_minutes);
      if (earlyError) {
        nextFieldErrors.early_clock_in_minutes = earlyError;
      } else {
        earlyClockInMinutes = parseEarlyClockInMinutes(editing.early_clock_in_minutes)!;
      }
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setActiveTab('app');
      setOpenGroupKey('zeit');
      return;
    }

    setSaving(true);
    try {
      const method = editing.id ? 'PATCH' : 'POST';
      const url = editing.id ? `/api/me/role-presets/${editing.id}` : '/api/me/role-presets';
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editing.name.trim(),
          description: editing.description.trim(),
          default_platform_access: editing.default_platform_access,
          capabilities: normalizeCaps(editing.capabilities),
          geofence_radius_m: geofenceRadiusM,
          early_clock_in_minutes: earlyClockInMinutes,
          clock_mode: parseClockMode(editing.clock_mode),
        }),
      });
      const data = (await res.json().catch(() => null)) as { error?: string; preset?: RolePreset } | null;
      if (!res.ok) throw new Error(data?.error ?? 'Rolle konnte nicht gespeichert werden.');
      await load();
      if (data?.preset?.id) {
        navigate(withInstancePath(roleSettingsPath(data.preset.id)), { replace: true });
      } else {
        closeEditor();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler.');
    } finally {
      setSaving(false);
    }
  };

  const requestRemovePreset = (preset: RolePreset) => {
    setError(null);
    setDeleteTarget(preset);
  };

  const updateCapability = (capKey: string, value: boolean) => {
    setEditing((current) => {
      if (!current) return current;
      const nextCaps: CapabilityMap = {
        ...current.capabilities,
        [capKey]: value,
      };
      if (!value && capKey.endsWith('.view')) {
        const editKey = `${capKey.slice(0, -5)}.edit`;
        if (editKey in nextCaps) nextCaps[editKey] = false;
        if (capKey === 'web.hr.view') {
          nextCaps['web.hr.approve'] = false;
          nextCaps['web.hr.internal'] = false;
        }
        if (capKey === 'web.rechnungen.view') {
          nextCaps['web.rechnungen.approve'] = false;
          nextCaps['web.rechnungen.datev'] = false;
        }
      }
      // Geofencing only exists on top of clock-in/out — turning that off drops it,
      // otherwise the shared implications would switch clock editing back on.
      if (!value && (capKey === 'app.zeit.edit' || capKey === 'app.zeit.view')) {
        for (const key of GEOFENCE_CAPABILITY_KEYS) nextCaps[key] = false;
      }
      return {
        ...current,
        capabilities: normalizeCaps(nextCaps),
      };
    });
  };

  const systemPresets = presets.filter((preset) => preset.is_system_fork);
  const customPresets = presets.filter((preset) => !preset.is_system_fork);
  const roleLimit = usage('customRolePresets');
  const rolesAtLimit = customPresets.length >= roleLimit.max;
  const roleMembers = editing?.id
    ? members
        .filter((member) => !member.invited && member.role_preset_id === editing.id)
        .sort((a, b) => roleMemberName(a).localeCompare(roleMemberName(b), 'de'))
    : [];
  const visibleGroups = activeTab === 'web' || !fieldAppEnabled ? WEB_GROUPS : APP_GROUPS;

  if (editing) {
    const webInactive = editing.default_platform_access === 'field_only';
    const webEnabled = countEnabledGroups(WEB_GROUPS, editing.capabilities, webInactive);
    const appEnabled = countEnabledGroups(APP_GROUPS, editing.capabilities);
    const webTotal = countTotalGroups(WEB_GROUPS);
    const appTotal = countTotalGroups(APP_GROUPS);
    const activeGroups = activeTab === 'web' || !fieldAppEnabled ? WEB_GROUPS : APP_GROUPS;
    const geofenceRadiusFieldError = fieldErrors.geofence_radius_m;
    const earlyClockInFieldError = fieldErrors.early_clock_in_minutes;
    return (
      <section className="flex h-full min-h-full flex-col bg-app-surface">
        {error ? (
          <SettingsErrorBanner message={error} onDismiss={dismissError} className="border-b border-x-0 border-t-0" />
        ) : null}

        <div className="shrink-0 border-b border-app-border px-5 py-4">
          <div className="flex items-center gap-2 text-sm text-app-text-subtle">
            <button type="button" onClick={closeEditor} className="hover:text-app-text-muted">
              Einstellungen
            </button>
            <span>›</span>
            <button type="button" onClick={closeEditor} className="hover:text-app-text-muted">
              Rollen & Rechte
            </button>
            <span>›</span>
            <span className="font-semibold text-app-text">{editing.name.trim() || 'Neue Rolle'}</span>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 lg:grid-cols-[400px_minmax(0,1fr)]">
          <aside className="border-b border-app-border bg-app-surface p-5 lg:border-b-0 lg:border-r">
            <div className="space-y-4">
              <div className="bg-app-surface">
                <label className="mb-4 block">
                  <span className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>Name</span>
                  <input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    className={SETTINGS_INPUT_CLASS}
                  />
                </label>
                <label className="block">
                  <span className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>Beschreibung <span className="text-slate-400">(optional)</span></span>
                  <textarea
                    value={editing.description}
                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    rows={4}
                    className={`${SETTINGS_TEXTAREA_CLASS} resize-none`}
                  />
                </label>
              </div>

              <div className="bg-app-surface">
                <div className={`mb-2 ${SETTINGS_FIELD_LABEL}`}>Default-Zugang</div>
                <div className="flex rounded-xl bg-app-surface-muted p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing({ ...editing, default_platform_access: 'office_and_field' });
                      const surface = initialSurfaceForAccess('office_and_field');
                      setActiveTab(surface.tab);
                      setOpenGroupKey(surface.groupKey);
                    }}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
                      editing.default_platform_access === 'office_and_field' ? 'bg-app-surface text-app-text shadow-sm' : 'text-app-text-subtle'
                    }`}
                  >
                    <Monitor className="h-4 w-4" />
                    Web & App
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing({ ...editing, default_platform_access: 'field_only' });
                      const surface = initialSurfaceForAccess('field_only');
                      setActiveTab(surface.tab);
                      setOpenGroupKey(surface.groupKey);
                    }}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
                      editing.default_platform_access === 'field_only' ? 'bg-app-surface text-app-text shadow-sm' : 'text-app-text-subtle'
                    }`}
                  >
                    <Smartphone className="h-4 w-4" />
                    Nur App
                  </button>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-app-text-subtle">
                  Legt fest, womit sich Nutzer mit dieser Rolle anmelden können. Bei `Nur App` bleiben Web-Rechte gespeichert, aber ohne Wirkung.
                </p>
              </div>

              {(editing.assigned_user_count ?? 0) > 0 ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm leading-relaxed text-amber-900">
                  <div>
                    <span className="font-semibold">{editing.assigned_user_count ?? 0} Nutzer</span>
                    {' '}nutzen diese Rolle. Änderungen wirken beim Speichern auf alle, außer bei individuellen Abweichungen.
                  </div>
                  {roleMembers.length ? (
                    <ul className="mt-2 list-disc space-y-0.5 pl-5">
                      {roleMembers.map((member) => {
                        const adapted =
                          listCapabilityDeviations(editing.capabilities, member.capability_overrides ?? {}).length > 0;
                        return (
                          <li
                            key={member.user_id}
                            title={adapted ? 'Individuelle Abweichungen von der Rolle' : undefined}
                          >
                            {roleMemberName(member)}
                            {adapted ? <span className="text-amber-700">· angepasst</span> : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
          </aside>

          <div className="flex min-h-0 min-w-0 flex-col bg-app-surface-muted">
            <div className="shrink-0 border-b border-app-border bg-app-surface px-5 py-3">
              <div className="flex flex-wrap items-center gap-5">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('web');
                    setOpenGroupKey('projekte');
                  }}
                  className={`inline-flex items-center gap-2 border-b-2 px-1 py-2 text-sm font-semibold ${
                    activeTab === 'web' ? 'border-blue-500 text-blue-700' : 'border-transparent text-app-text-subtle'
                  }`}
                >
                  {webEnabled > 0 ? <Monitor className="h-4 w-4" /> : null}
                  Web (Büro)
                  <span className="text-[11px] font-medium text-slate-400">{webEnabled}/{webTotal}</span>
                </button>
                {fieldAppEnabled ? (
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('app');
                    setOpenGroupKey('zeit');
                  }}
                  className={`inline-flex items-center gap-2 border-b-2 px-1 py-2 text-sm font-semibold ${
                    activeTab === 'app' ? 'border-blue-500 text-blue-700' : 'border-transparent text-app-text-subtle'
                  }`}
                >
                  {appEnabled > 0 ? <Smartphone className="h-4 w-4" /> : null}
                  App (Außendienst)
                  <span className="text-[11px] font-medium text-slate-400">{appEnabled}/{appTotal}</span>
                </button>
                ) : null}
                <div className="ml-auto text-xs text-slate-400">
                  „Bearbeiten“ aktiviert automatisch „Ansehen“
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto app-scrollbar px-5 py-4">
              {activeGroups.map((group) => {
                const webDisabled = editing.default_platform_access === 'field_only' && activeTab === 'web';
                const expanded = openGroupKey === group.key;
                const enabledCount = group.perms.filter((perm) => Boolean(editing.capabilities[perm.key])).length;
                const statusLabel = webDisabled
                  ? 'Ohne Wirkung'
                  : !enabledCount
                    ? 'Kein Zugriff'
                    : enabledCount === group.perms.length
                      ? statusLabelForGroup(group, editing.capabilities)
                      : statusLabelForGroup(group, editing.capabilities);
                const statusClass = webDisabled
                  ? 'bg-app-surface-muted text-app-text-subtle'
                  : !enabledCount
                    ? 'bg-app-surface-muted text-app-text-subtle'
                    : 'bg-blue-50 text-blue-700';
                const iconClass = webDisabled || !enabledCount
                  ? 'bg-app-surface-muted text-app-text-subtle'
                  : 'bg-blue-50 text-blue-600';
                return (
                  <div key={group.key} className={`overflow-hidden rounded-2xl border border-app-border bg-app-surface ${webDisabled ? 'opacity-60' : ''}`}>
                    <button
                      type="button"
                      onClick={() => setOpenGroupKey((current) => (current === group.key ? '' : group.key))}
                      className="flex w-full items-center gap-3 px-4 py-4 text-left"
                    >
                      <ChevronRight className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconClass}`}>
                        {React.createElement(GROUP_ICON_MAP[group.key] ?? ShieldCheck, { className: 'h-4 w-4' })}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-app-text">{group.label}</div>
                        <div className="truncate text-xs text-app-text-subtle">{group.description}</div>
                      </div>
                      <span className={`rounded-md px-2 py-1 text-[11px] font-medium ${statusClass}`}>{statusLabel}</span>
                    </button>

                    {expanded ? (
                      <div className="border-t border-app-border px-4 py-2">
                        {group.perms.map((perm, index) => (
                          <div
                            key={perm.key}
                            className={`flex items-center justify-between gap-3 rounded-lg px-1 py-3 ${index > 0 ? 'border-t border-app-border' : ''}`}
                          >
                            <div className="min-w-0">
                              <div className={SETTINGS_FIELD_LABEL}>{perm.label}</div>
                              <div className="text-xs text-app-text-subtle">{perm.hint}</div>
                            </div>
                            <Toggle
                              active={Boolean(editing.capabilities[perm.key])}
                              disabled={webDisabled}
                              onClick={() => updateCapability(perm.key, !editing.capabilities[perm.key])}
                            />
                          </div>
                        ))}
                        {group.key === 'zeit' && hasGeofenceCapability(editing.capabilities) ? (
                          <div className="border-t border-app-border px-1 py-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className={SETTINGS_FIELD_LABEL}>Radius (m)</div>
                                <p className="text-xs text-app-text-subtle">
                                  Umkreis für Einstempeln (erster Termin) und Ausstempeln (letzter Termin), {MIN_GEOFENCE_RADIUS_M}–{MAX_GEOFENCE_RADIUS_M} m.
                                  Einzelne Nutzer können davon abweichen.
                                </p>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <input
                                  type="number"
                                  min={MIN_GEOFENCE_RADIUS_M}
                                  max={MAX_GEOFENCE_RADIUS_M}
                                  step={10}
                                  value={editing.geofence_radius_m}
                                  onChange={(e) => {
                                    setFieldErrors((current) => ({ ...current, geofence_radius_m: undefined }));
                                    setEditing({
                                      ...editing,
                                      geofence_radius_m: e.target.value === '' ? '' : Number(e.target.value),
                                    });
                                  }}
                                  className={`w-24 ${SETTINGS_INPUT_CLASS} outline-none ${invalidNumberInputClass(Boolean(geofenceRadiusFieldError))}`}
                                  aria-label="Geofence-Radius in Metern"
                                  aria-invalid={Boolean(geofenceRadiusFieldError)}
                                />
                                <span className="text-sm text-app-text-subtle">m</span>
                              </div>
                            </div>
                            {geofenceRadiusFieldError ? (
                              <p className="mt-2 text-xs text-red-600">{geofenceRadiusFieldError}</p>
                            ) : null}
                          </div>
                        ) : null}
                        {group.key === 'zeit' && Boolean(editing.capabilities['app.zeit.edit']) ? (
                          <div className="border-t border-app-border px-1 py-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className={SETTINGS_FIELD_LABEL}>Frühestens vor Schichtstart (Min.)</div>
                                <p className="text-xs text-app-text-subtle">
                                  Einstempeln ab {MIN_EARLY_CLOCK_IN_MINUTES}–{MAX_EARLY_CLOCK_IN_MINUTES} Min. vor dem ersten Termin.
                                  Im Vorlauf zählt die Startzeit erst ab Schichtstart; bis Ende des letzten Termins möglich. Ausstempeln bleibt frei.
                                </p>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <input
                                  type="number"
                                  min={MIN_EARLY_CLOCK_IN_MINUTES}
                                  max={MAX_EARLY_CLOCK_IN_MINUTES}
                                  step={1}
                                  value={editing.early_clock_in_minutes}
                                  onChange={(e) => {
                                    setFieldErrors((current) => ({ ...current, early_clock_in_minutes: undefined }));
                                    setEditing({
                                      ...editing,
                                      early_clock_in_minutes: e.target.value === '' ? '' : Number(e.target.value),
                                    });
                                  }}
                                  className={`w-24 ${SETTINGS_INPUT_CLASS} outline-none ${invalidNumberInputClass(Boolean(earlyClockInFieldError))}`}
                                  aria-label="Früheste Einstempelung in Minuten vor Schichtstart"
                                  aria-invalid={Boolean(earlyClockInFieldError)}
                                />
                                <span className="text-sm text-app-text-subtle">Min.</span>
                              </div>
                            </div>
                            {earlyClockInFieldError ? (
                              <p className="mt-2 text-xs text-red-600">{earlyClockInFieldError}</p>
                            ) : null}
                          </div>
                        ) : null}
                        {group.key === 'zeit' && Boolean(editing.capabilities['app.zeit.edit']) ? (
                          <div className="border-t border-app-border px-1 py-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className={SETTINGS_FIELD_LABEL}>Stempelmodus</div>
                                <p className="text-xs text-app-text-subtle">
                                  {CLOCK_ACTIVITY_MODE_AVAILABLE
                                    ? 'Ganzer Tag: ein Stempel pro Tag. Je Termin: jeder Einsatz wird ein eigener Zeiteintrag.'
                                    : 'Aktuell nur ganzer Tag. Stempeln je Termin folgt nach der nächsten Datenbank-Migration.'}
                                  {editing.forked_from_key === 'office_full_access'
                                    ? ' Gilt auch für Inhaber und Admins (Vollzugriff).'
                                    : ''}
                                </p>
                              </div>
                              <div className="w-44 shrink-0">
                                <AppSelectDropdown
                                  value={parseClockMode(editing.clock_mode)}
                                  onChange={(value) =>
                                    setEditing({ ...editing, clock_mode: parseClockMode(value) })
                                  }
                                  options={
                                    CLOCK_ACTIVITY_MODE_AVAILABLE
                                      ? [
                                          { value: 'day', label: 'Ganzer Tag' },
                                          { value: 'activity', label: 'Je Termin' },
                                        ]
                                      : [{ value: 'day', label: 'Ganzer Tag' }]
                                  }
                                  triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                                  disabled={webDisabled}
                                />
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="flex shrink-0 items-center justify-between border-t border-app-border bg-app-surface px-4 py-3">
              <div className="text-sm text-app-text-subtle">
                Effektiv: {countEnabledGroups(WEB_GROUPS, editing.capabilities, editing.default_platform_access === 'field_only')} Web-Bereiche · {countEnabledGroups(APP_GROUPS, editing.capabilities)} App-Bereiche
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeEditor}
                  className={SETTINGS_BTN_OUTLINE}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  disabled={saving || !editing.name.trim()}
                  onClick={() => {
                    void persist();
                  }}
                  className={`${SETTINGS_BTN_PRIMARY} gap-2`}
                >
                  {!saving ? <Check className="h-4 w-4" /> : null}
                  {saving ? 'Speichern…' : `Speichern${editing.assigned_user_count ? ` & auf ${editing.assigned_user_count} Nutzer anwenden` : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const renderCard = (preset: RolePreset) => {
    const webGroupCount = countEnabledGroups(WEB_GROUPS, preset.capabilities, preset.default_platform_access === 'field_only');
    const appGroupCount = countEnabledGroups(APP_GROUPS, preset.capabilities);
    return (
      <button
        key={preset.id}
        type="button"
        onClick={() => openEdit(preset)}
        className="w-full rounded-2xl border border-app-border bg-app-surface p-4 text-left"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {preset.default_platform_access === 'field_only' ? (
                <div className="rounded-full bg-amber-50 p-1.5 text-amber-600">
                  <HardHat className="h-4 w-4" />
                </div>
              ) : (
                <div className="rounded-full bg-emerald-50 p-1.5 text-emerald-600">
                  <Building2 className="h-4 w-4" />
                </div>
              )}
              <h3 className="truncate text-base font-semibold text-app-text">{preset.name}</h3>
              <Chip label={preset.default_platform_access === 'field_only' ? 'Nur App' : 'Web & App'} tone={preset.default_platform_access === 'field_only' ? 'amber' : 'green'} />
            </div>
            <p className="mt-2 text-sm leading-relaxed text-app-text-subtle">{preset.description || 'Keine Beschreibung hinterlegt.'}</p>
          </div>
          <div ref={openMenuId === preset.id ? openMenuRef : null} className="relative shrink-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuId((current) => (current === preset.id ? null : preset.id));
              }}
              className="rounded-lg p-2 text-slate-400 hover:bg-app-hover hover:text-app-text-muted"
              aria-label="Aktionen"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {openMenuId === preset.id ? (
              <div className="absolute right-0 top-10 z-10 min-w-[160px] rounded-xl border border-app-border bg-app-surface p-1 shadow-lg">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuId(null);
                    if (rolesAtLimit) return;
                    openClone(preset);
                  }}
                  disabled={rolesAtLimit}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover disabled:opacity-40"
                >
                  <Copy className="h-4 w-4" />
                  Duplizieren
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuId(null);
                    requestRemovePreset(preset);
                  }}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Rolle löschen
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {webGroupCount > 0 ? <Chip label={`${webGroupCount} Web`} tone="blue" icon={Monitor} /> : null}
          {appGroupCount > 0 ? <Chip label={`${appGroupCount} App`} tone="violet" icon={Smartphone} /> : null}
          <Chip label={`${preset.assigned_user_count ?? 0} Nutzer`} tone="slate" icon={Users} />
        </div>
      </button>
    );
  };

  return (
    <section className="space-y-5 px-3 py-3 md:px-6 md:py-6">
      {error ? (
        <SettingsErrorBanner message={error} onDismiss={dismissError} className="rounded-lg" />
      ) : null}

      <div>
        <div className="flex flex-wrap items-start justify-between gap-4 px-1 py-1">
          <div>
            <h2 className="text-base font-bold text-app-text">Rollen & Rechte</h2>
            <p className="mt-1 max-w-3xl text-sm text-app-text-muted">
              Lege fest, was Nutzer im Büro (Web) und in der App sehen und bearbeiten dürfen. Rechte gelten pro Instanz und können über Rollen gesteuert werden.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={openNew}
              disabled={rolesAtLimit}
              className={`${SETTINGS_BTN_PRIMARY} gap-2 disabled:opacity-40`}
            >
              <Plus className="h-4 w-4" />
              Neue Rolle
            </button>
          </div>
        </div>

        <div className="space-y-6 pt-5">
          {loading ? (
            <div className="rounded-xl border border-dashed border-app-border-strong bg-app-surface px-4 py-10">
              <InlineLoadingState label="Rollen werden geladen…" />
            </div>
          ) : (
            <>
              <div>
                <div className={`mb-3 ${SETTINGS_FIELD_LABEL}`}>
                  Standard Vorlagen
                </div>
                <div className="grid gap-4 xl:grid-cols-2">
                  {systemPresets.map(renderCard)}
                </div>
              </div>

              <div>
                <div className="mb-3 flex items-center gap-3">
                  <div className={SETTINGS_FIELD_LABEL}>
                    Eigene Rollen
                  </div>
                  <FeatureLimitHint
                    limitKey="customRolePresets"
                    used={customPresets.length}
                    max={roleLimit.max}
                    ready={limitsReady}
                  />
                </div>
                <div className="grid gap-4 xl:grid-cols-2">
                  {customPresets.map(renderCard)}
                  <button
                    type="button"
                    onClick={openNew}
                    disabled={rolesAtLimit}
                      className="flex min-h-[84px] items-center gap-4 rounded-2xl border border-dashed border-app-border-strong bg-app-surface-muted px-5 text-left text-app-text-subtle transition-colors hover:border-pipedrive-blue hover:text-pipedrive-blue disabled:pointer-events-none disabled:opacity-40"
                  >
                      <div className="flex h-6 w-6 items-center justify-center text-slate-400">
                        <Plus className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-app-text-muted">Neue Rolle anlegen</div>
                        <div className="text-sm text-app-text-subtle">leer oder als Kopie einer Vorlage</div>
                      </div>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-app-border bg-app-surface-muted px-4 py-3 text-sm text-app-text-subtle">
        <div className="flex items-start gap-3">
          <Users className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          <div>
            Änderungen an einer Rolle wirken für alle Nutzer mit dieser Rolle. Individuelle Abweichungen bleiben bestehen und werden in der Nutzeransicht als angepasst dargestellt.
          </div>
        </div>
      </div>

      <DeleteRolePresetDialog
        open={Boolean(deleteTarget)}
        preset={deleteTarget}
        otherPresets={presets.map((entry) => ({ id: entry.id, name: entry.name }))}
        onClose={() => setDeleteTarget(null)}
        onDeleted={async () => {
          if (deleteTarget && editing?.id === deleteTarget.id) closeEditor();
          await load();
        }}
      />
    </section>
  );
}
